import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './dialog';

const vDialogContentVariants = cva(
  'flex flex-col gap-0 overflow-hidden border-border/50 p-0',
  {
    variants: {
      size: {
        sm: 'max-w-md',
        md: 'max-w-4xl max-h-[90vh]',
        lg: 'max-w-5xl max-h-[90vh]',
        xl: 'h-[90vh] w-[95vw] max-w-7xl sm:max-w-7xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

type VDialogProps = VariantProps<typeof vDialogContentVariants> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required for a11y. Set `titleHidden` to render the title as `sr-only`. */
  title: string;
  titleHidden?: boolean;
  description?: string;
  descriptionHidden?: boolean;
  /** 'inline' (default): beschrijving naast de titel. 'stacked': beschrijving onder de titel. */
  headerLayout?: 'inline' | 'stacked';
  /** Content rendered left-aligned in the header row, before the title. */
  headerStart?: React.ReactNode;
  /** Toolbar content rendered right-aligned in the header row, next to the title. */
  header?: React.ReactNode;
  /** Content rendered below the body, separated by a top border. */
  footer?: React.ReactNode;
  /** Escape-hatch for extra classes on the underlying DialogContent. */
  contentClassName?: string;
  children: React.ReactNode;
};

/**
 * SafeZone modal dialog with sensible defaults. Prefer this over the raw `Dialog`/`DialogContent`
 * primitives — the underlying shadcn components remain exported from `@vrzl/ui` for edge cases
 * that cannot be expressed via props or `contentClassName`.
 */
export const VDialog = React.forwardRef<HTMLDivElement, VDialogProps>(
  function VDialog(
    {
      open,
      onOpenChange,
      size,
      title,
      titleHidden,
      description,
      descriptionHidden,
      headerLayout = 'inline',
      headerStart,
      header,
      footer,
      contentClassName,
      children,
    },
    ref,
  ) {
    const showVisibleTitle = !titleHidden;
    const showVisibleDescription = Boolean(description) && !descriptionHidden;
    const showHeaderRow =
      showVisibleTitle || Boolean(headerStart) || Boolean(header);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          ref={ref}
          className={cn(vDialogContentVariants({ size }), contentClassName)}
        >
          {!showVisibleTitle && (
            <DialogTitle className="sr-only">{title}</DialogTitle>
          )}
          {description && !showVisibleDescription && (
            <DialogDescription className="sr-only">{description}</DialogDescription>
          )}

          {showHeaderRow && (
            <div
              className={cn(
                'border-b border-border/50 px-4 pr-12 bg-white',
                headerLayout === 'stacked'
                  ? 'flex flex-col gap-0.5 py-3'
                  : 'flex items-center gap-3 py-2',
              )}
            >
              {headerStart}
              {showVisibleTitle && <DialogTitle>{title}</DialogTitle>}
              {showVisibleDescription && (
                <DialogDescription>{description}</DialogDescription>
              )}
              {header && <div className="ml-auto flex items-center gap-2">{header}</div>}
            </div>
          )}

          {children}

          {footer && (
            <div className="border-t border-border/50 px-4 py-2">{footer}</div>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);
VDialog.displayName = 'VDialog';

export { vDialogContentVariants };
export type { VDialogProps };
